import { decodeFunctionResult, encodeFunctionData, type Address, type Hex } from "viem";
import type { BootstrapResponse } from "../api/schemas";
import { ELYSIUM, elysiumBootstrapReasons, elysiumOwnerRedeemEnabled, sameIdentity } from "../config/elysium";
import { encodeRedeemCall, ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import type { Eip1193Provider } from "./eip1193";
import { currentChainId } from "./eip1193";
import { readNftState } from "./nft-lifecycle";
import { observeElysium } from "./elysium-readiness";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { observeRedeemDeployment } from "./redeem-fee-route";
import { createRedemptionDiagnostics, type RedemptionDiagnostics } from "./redemption-diagnostics";

type RedeemReadOptions = RpcReadOptions & { diagnostics?: RedemptionDiagnostics };

export type DirectRedeemRoute = Readonly<{ kind: "ELYSIUM_DIRECT_VAULT"; chainId: 99901;
  collection: Address; sourceToken: Address; vault: Address; instanceId: Hex; deploymentRevision: string;
  environment: "production" | "development" | "test"; support: "CONFIGURED_READ_ONLY" }>;
export function selectElysiumDirectRedeem(bootstrap: BootstrapResponse, sourceToken: string): DirectRedeemRoute {
  const reasons = elysiumBootstrapReasons(bootstrap);
  if (reasons.length || !sameIdentity(sourceToken, ELYSIUM.token)) throw new Error(reasons.join(", ") || "ELYSIUM_SOURCE_MISMATCH");
  return Object.freeze({ kind: "ELYSIUM_DIRECT_VAULT", chainId: 99901, collection: ELYSIUM.collection,
    sourceToken: ELYSIUM.token, vault: ELYSIUM.vault, instanceId: ELYSIUM.instanceId,
    environment: bootstrap.environment, support: "CONFIGURED_READ_ONLY", deploymentRevision: `ELYSIUM_V3:${ELYSIUM.instanceId}` });
}

/** Owner/source/final state and fresh settled-vault preview. No legacy curve, API or media prerequisite. */
export async function readElysiumRedeemPreview(provider: Eip1193Provider,
  input: { account: Address; tokenId: bigint; minimum: bigint; route: DirectRedeemRoute },
  guard: () => void, options: RedeemReadOptions = {}) {
  const diagnostics = options.diagnostics ?? createRedemptionDiagnostics(options.signal);
  return diagnostics.step("Read redemption preview", async () => {
    guard();
    if (input.tokenId < 0n || input.tokenId >= 1n << 256n || input.minimum < 0n || input.minimum >= 1n << 256n ||
      input.route.kind !== "ELYSIUM_DIRECT_VAULT" || input.route.chainId !== 99901 ||
      !sameIdentity(input.route.collection, ELYSIUM.collection) || !sameIdentity(input.route.sourceToken, ELYSIUM.token) ||
      !sameIdentity(input.route.vault, ELYSIUM.vault) || input.route.instanceId !== ELYSIUM.instanceId)
      throw new Error("DIRECT_REDEEM_CONTEXT_MISMATCH");
    const observation = await diagnostics.step("Verify network and contracts", () => observeElysium(provider, guard, options));
    const block = `0x${observation.blockNumber.toString(16)}` as Hex;
    const deployment = await diagnostics.step("Verify collection implementation", () => observeRedeemDeployment(provider, ELYSIUM.collection, block, guard, options));
    if (!sameIdentity(deployment.implementation, ELYSIUM.implementation) ||
      !sameIdentity(deployment.implementationCodeHash, ELYSIUM.implementationCodeHash))
      throw new Error("DIRECT_REDEEM_DEPLOYMENT_MISMATCH");
    const state = await diagnostics.step("Read NFT state", () => readNftState(provider, { chainId: 99901, collection: ELYSIUM.collection,
      tokenId: input.tokenId, block: { number: block, hash: observation.blockHash } }, guard, options));
    if (state.status !== 3 || !sameIdentity(state.sourceToken, ELYSIUM.token)) throw new Error("NFT_NOT_FINALIZED_OR_SOURCE_CHANGED");
    const read = async (functionName: "ownerOf" | "previewRedeem" | "rewardDenominatorFor" | "FEE_VAULT" | "INSTANCE_ID") => {
      const data = functionName === "ownerOf" || functionName === "previewRedeem" || functionName === "rewardDenominatorFor" ?
        encodeFunctionData({ abi: ponsRewardNftAbi, functionName, args: [input.tokenId] }) : encodeFunctionData({ abi: ponsRewardNftAbi, functionName });
      const value = await diagnostics.step(`Read ${functionName}`, () => readRpc(provider, { method: "eth_call", params: [{ to: ELYSIUM.collection, data }, block] }, options));
      guard(); if (typeof value !== "string") throw new Error("DIRECT_REDEEM_READ_INVALID"); return value as Hex;
    };
    const [ownerData, previewData, vaultData, instanceData] = await Promise.all([read("ownerOf"), read("previewRedeem"), read("FEE_VAULT"), read("INSTANCE_ID")]);
    const owner = decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "ownerOf", data: ownerData });
    const [source, bps, pool, payout, redeemable] = decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "previewRedeem", data: previewData });
    if (state.policyVersion === 2n && decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "rewardDenominatorFor",
      data: await read("rewardDenominatorFor") }) !== BigInt(state.rewardDenominator)) throw new Error("DIRECT_REDEEM_BINDING_MISMATCH");
    if (!sameIdentity(owner, input.account)) throw new Error("UnauthorizedRedeemer: Connect the current NFT owner.");
    if (!sameIdentity(decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "FEE_VAULT", data: vaultData }), ELYSIUM.vault) ||
      decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "INSTANCE_ID", data: instanceData }) !== ELYSIUM.instanceId ||
      !sameIdentity(source, ELYSIUM.token) || bps !== state.rewardRate || payout !== pool * BigInt(bps) / BigInt(state.rewardDenominator))
      throw new Error("DIRECT_REDEEM_BINDING_MISMATCH");
    if (!redeemable || payout <= 0n) throw new Error("PayoutIsZero: No positive finalized payout is available.");
    if (payout < input.minimum) throw new Error("PayoutBelowMinimum: Review a new quote; the approved floor was not lowered.");
    const confirmed = await diagnostics.step("Recheck preview block", () => readRpc(provider, { method: "eth_getBlockByNumber", params: [block, false] }, options)) as { hash?: string } | null;
    guard();
    if (!sameIdentity(confirmed?.hash, observation.blockHash) ||
      await diagnostics.step("Recheck preview network", () => currentChainId(provider, options)) !== 99901)
      throw new Error("DIRECT_REDEEM_SNAPSHOT_CHANGED");
    guard(); return { payout, pool, owner, rewardBps: state.policyVersion === 1n ? bps : null,
      rewardRate: bps, rewardDenominator: state.rewardDenominator, observation, deployment };
  });
}

/** Recheck after exact-call simulation, before the final wallet chain/time fence. */
export async function recheckElysiumRedeemDeployment(provider: Eip1193Provider,
  preview: Pick<Awaited<ReturnType<typeof readElysiumRedeemPreview>>, "deployment" | "observation">,
  assertCurrent: () => void, options: RpcReadOptions = {}) {
  const guard = () => { options.signal?.throwIfAborted(); assertCurrent(); };
  guard();
  const header = await readRpc(provider, { method: "eth_getBlockByNumber", params: ["latest", false] }, options) as { hash?: string } | null;
  guard();
  if (typeof header?.hash !== "string" || !/^0x[0-9a-f]{64}$/i.test(header.hash) || BigInt(header.hash) === 0n)
    throw new Error("DIRECT_REDEEM_SNAPSHOT_CHANGED");
  const current = await observeRedeemDeployment(provider, ELYSIUM.collection,
    { blockHash: header.hash as Hex, requireCanonical: true }, guard, options);
  if (!sameIdentity(current.implementation, ELYSIUM.implementation) ||
    !sameIdentity(current.implementationCodeHash, ELYSIUM.implementationCodeHash) ||
    !sameIdentity(current.collectionCodeHash, preview.deployment.collectionCodeHash))
    throw new Error("DIRECT_REDEEM_DEPLOYMENT_MISMATCH");
  const confirmed = await readRpc(provider, { method: "eth_getBlockByNumber",
    params: [`0x${preview.observation.blockNumber.toString(16)}`, false] }, options) as { hash?: string } | null;
  guard();
  if (!sameIdentity(confirmed?.hash, preview.observation.blockHash) || await currentChainId(provider, options) !== ELYSIUM.chainId)
    throw new Error("DIRECT_REDEEM_SNAPSHOT_CHANGED");
  guard();
}

export type DirectRedeemIdentity = { tokenId: bigint; minPayoutWei: bigint; deadline: bigint; reviewedAt: bigint;
  route: DirectRedeemRoute; getBootstrap(): BootstrapResponse | null };
export function assertDirectRedeemCall(input: { account: Address; chainId: number; to: Address; data: Hex; valueWei: bigint;
  directRedeemIdentity?: DirectRedeemIdentity }) {
  const identity = input.directRedeemIdentity;
  if (!identity || input.chainId !== 99901 || input.valueWei !== 0n || !sameIdentity(input.to, ELYSIUM.collection) ||
    identity.minPayoutWei <= 0n || identity.reviewedAt < 0n || identity.deadline !== identity.reviewedAt + 300n ||
    input.data.toLowerCase() !== encodeRedeemCall(identity).toLowerCase() || !elysiumOwnerRedeemEnabled(identity.getBootstrap()))
    throw new Error("DIRECT_REDEEM_POLICY_OR_REVIEW_UNAVAILABLE");
  return identity;
}

export async function simulateElysiumRedeem(provider: Eip1193Provider,
  input: { account: Address; tokenId: bigint; minPayoutWei: bigint; deadline: bigint }, guard: () => void, options: RedeemReadOptions = {}) {
  const diagnostics = options.diagnostics ?? createRedemptionDiagnostics(options.signal);
  return diagnostics.step("Simulate redemption", async () => {
    guard();
    if (input.minPayoutWei <= 0n || await currentChainId(provider, options) !== 99901) throw new Error("DIRECT_REDEEM_CONTEXT_MISMATCH");
    guard();
    const result = await readRpc(provider, { method: "eth_call", params: [{ from: input.account, to: ELYSIUM.collection,
      data: encodeRedeemCall(input), value: "0x0" }, "latest"] }, options);
    guard();
    if (result !== "0x" || await currentChainId(provider, options) !== 99901) throw new Error("DIRECT_REDEEM_SIMULATION_INVALID");
    guard();
  });
}
